import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"
import {
  createToolPill,
  completeToolPill,
  updateToolsSummary
} from "../helpers/streaming_message"

// Manages ActionCable subscription for real-time agent streaming.
// Markdown is rendered server-side — the client receives pre-rendered HTML.
export default class extends Controller {
  static targets = ["output", "typingIndicator", "sessionReady", "messageTemplate"]
  static values = { sessionId: String }

  connect() {
    this.currentMessageId = null
    this.outputElement = null
    this.streamTextElement = null
    this.toolsContainer = null
    this.toolsDetails = null
    this.streamFinished = false

    if (this.sessionIdValue) this.subscribe()
  }

  sessionIdValueChanged() {
    if (this.sessionIdValue && !this.subscription) this.subscribe()
  }

  sessionReadyTargetConnected(el) {
    const sessionId = el.dataset.sessionId
    this.streamFinished = false
    this.resetStreamingState()
    if (sessionId && !this.subscription) {
      this.sessionIdValue = sessionId
      this.subscribe()
    }
    el.remove()
  }

  subscribe() {
    if (this.subscription) return
    this.streamFinished = false
    this.subscription = consumer.subscriptions.create(
      { channel: "AgentStreamChannel", session_id: this.sessionIdValue },
      {
        received: (data) => this.handleMessage(data),
        disconnected: () => {
          if (this.outputElement) {
            this.showReconnectingIndicator()
            this.reconnectTimer = setTimeout(() => window.location.reload(), 5000)
          }
        },
      }
    )
  }

  disconnect() {
    clearTimeout(this.reconnectTimer)
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }

  handleMessage(data) {
    if (this.streamFinished) return

    switch (data.type) {
      case "html": this.handleHtml(data); break
      case "clear": this.handleClear(); break
      case "done": this.handleComplete(); break
      case "tool_start": this.handleToolStart(data); break
      case "tool_result": this.handleToolResult(data); break
      case "error": this.handleError(); break
    }
  }

  // --- Event handlers ---

  handleHtml(data) {
    this.ensureStreamingElement()
    if (typeof data.content === "string") {
      // Server-side sanitized: render_chat_markdown uses Commonmarker + Rails sanitize()
      this.streamTextElement.innerHTML = data.content
      this.scrollToBottom()
    }
    if (this.outputElement) this.outputElement.classList.remove("hidden")
  }

  handleClear() {
    if (this.streamTextElement) this.streamTextElement.innerHTML = ""
  }

  handleComplete() {
    this.streamFinished = true
    clearTimeout(this.reconnectTimer)
    this.resetStreamingState()
    this.dispatch("done")
  }

  handleToolStart(data) {
    this.ensureStreamingElement()
    if (this.toolsContainer) {
      if (this.toolsDetails) this.toolsDetails.classList.remove("hidden")
      this.toolsContainer.appendChild(createToolPill(data.tool_name))
      updateToolsSummary(this.toolsDetails, this.toolsContainer)
    }
    this.scrollToBottom()
  }

  handleToolResult(data) {
    if (this.toolsContainer) completeToolPill(this.toolsContainer, data.tool_name)
  }

  handleError() {
    this.streamFinished = true
    if (this.streamTextElement) {
      const errorEl = document.createElement("p")
      errorEl.classList.add("text-sm", "text-content-danger", "mt-2")
      errorEl.textContent = "Something went wrong. Please try again."
      this.streamTextElement.appendChild(errorEl)
      const stopBtn = this.outputElement?.querySelector("[data-action*='stopGenerating']")
      if (stopBtn) stopBtn.remove()
    } else {
      this.resetStreamingState()
    }
    this.dispatch("done")
  }

  // --- DOM management ---

  ensureStreamingElement() {
    if (this.outputElement) return

    if (this.hasTypingIndicatorTarget) {
      this.typingIndicatorTarget.classList.add("hidden")
    }

    const clone = this.messageTemplateTarget.content.cloneNode(true).firstElementChild
    clone.id = `streaming-${Date.now()}`
    this.currentMessageId = clone.id

    this.outputTarget.appendChild(clone)
    this.outputElement = clone
    this.streamTextElement = clone.querySelector("[data-stream-role='textContent']")
    this.toolsDetails = clone.querySelector("[data-stream-role='toolsDetails']")
    this.toolsContainer = clone.querySelector("[data-stream-role='toolsContainer']")
    this.scrollToBottom()
  }

  resetStreamingState() {
    if (this.outputElement) this.outputElement.remove()
    this.currentMessageId = null
    this.outputElement = null
    this.streamTextElement = null
    this.toolsContainer = null
    this.toolsDetails = null
  }

  async stopGenerating(event) {
    event.currentTarget.disabled = true
    event.currentTarget.textContent = "Stopping..."

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const cancelPath = this.element.dataset.cancelPath
    if (!cancelPath) return

    try {
      await fetch(cancelPath, {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken },
      })
    } catch (error) {
      console.error("Stop generating error:", error)
    }
  }

  showReconnectingIndicator() {
    if (!this.streamTextElement) return
    const el = document.createElement("p")
    el.classList.add("text-xs", "text-amber-500", "mt-2", "animate-pulse")
    el.textContent = "Connection lost. Reconnecting..."
    this.streamTextElement.appendChild(el)
  }

  scrollToBottom() {
    this.scrollContainer ??= this.outputTarget.closest(".overflow-y-auto")
    if (this.scrollContainer) {
      this.scrollContainer.scrollTop = this.scrollContainer.scrollHeight
    }
  }
}
